import { useSite } from "@/lib/site-context";
import { useEffect, useRef, useState } from "react";

export function SiteSwitcher({ collapsed }: { collapsed: boolean }) {
	const { site, sites, setSite } = useSite();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	if (collapsed) {
		return (
			<div className="relative" ref={ref}>
				<button
					type="button"
					onClick={() => setOpen(!open)}
					className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-surface-2"
					title={site.name}
				>
					<span
						className="inline-block h-3 w-3 rounded-full"
						style={{ backgroundColor: site.color }}
					/>
				</button>
				{open && (
					<div className="absolute left-full top-0 z-50 ml-2 w-48 rounded-lg border border-line bg-surface py-1 shadow-lg">
						{sites.map((s) => (
							<button
								key={s.id}
								type="button"
								onClick={() => {
									setSite(s);
									setOpen(false);
								}}
								className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-2 ${
									s.id === site.id ? "bg-surface font-medium" : ""
								}`}
							>
								<span
									className="inline-block h-2.5 w-2.5 rounded-full"
									style={{ backgroundColor: s.color }}
								/>
								{s.name}
							</button>
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="relative" ref={ref}>
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex w-full items-center gap-2.5 rounded-lg border border-line bg-surface px-3 py-2 text-left text-sm transition-colors hover:bg-surface-2"
			>
				<span
					className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
					style={{ backgroundColor: site.color }}
				/>
				<span className="flex-1 truncate font-medium text-content">{site.name}</span>
				<svg
					className={`h-4 w-4 shrink-0 text-muted transition-transform ${open ? "rotate-180" : ""}`}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
				</svg>
			</button>
			{open && (
				<div className="absolute left-0 right-0 z-50 mt-1 rounded-lg border border-line bg-surface py-1 shadow-lg">
					{sites.map((s) => (
						<button
							key={s.id}
							type="button"
							onClick={() => {
								setSite(s);
								setOpen(false);
							}}
							className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-surface-2 ${
								s.id === site.id ? "bg-primary-soft font-medium text-primary" : "text-content"
							}`}
						>
							<span
								className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
								style={{ backgroundColor: s.color }}
							/>
							<span className="flex min-w-0 flex-1 flex-col">
								<span className="truncate">{s.name}</span>
								<span className="truncate text-[11px] text-muted">{s.domain}</span>
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
